/**
 * Rival-systemet (fase 5): Kapelle Damm som aktiv motstander.
 * Holder økt-tilstand for besøk på rival-etasjen, heismøter,
 * informasjonslekkasje (spionasje/hevn-vekting) og rivalens trekk.
 * Tekst- og vektdata bor i data/npcs.ts, data/rivalDialog.ts og
 * data/events.ts — dette er ren mekanikk.
 *
 * MVP-merknad: tilstanden lever i minnet for inneværende økt.
 */

export interface RivalBud {
  /** Manuset rivalen byr på. */
  isbn: string;
  tittel: string;
  /** Siste spilldag spilleren kan matche budet. */
  fristDag: number;
}

export interface RivalLansering {
  sjanger: string;
  /** Demper spillerens salg i samme sjanger til og med denne dagen. */
  tilDag: number;
  /** Multiplikator på salget (0.8 = -20 %). */
  faktor: number;
}

export class RivalSystem {
  private besokTeller = 0;
  private aggressivTilDag = 0;
  private hevnTilDag = 0;
  private aktivtBud: RivalBud | null = null;
  private aktiveLanseringer: RivalLansering[] = [];
  private konfrontasjonVenter = false;

  constructor(private rng: () => number = Math.random) {}

  // ---- 5A: besøk på etasjen ----

  /** Registrerer et besøk i KD-resepsjonen. Returnerer besøksnummeret. */
  registrerBesok(): number {
    this.besokTeller += 1;
    return this.besokTeller;
  }

  get antallBesok(): number {
    return this.besokTeller;
  }

  // ---- 5B: heismøter ----

  /**
   * Ruller om rivalen står i heisen. Kalles bare når timeplanen sier
   * at rivalen er i heisvinduet (data/schedules.ts: erIHeisen).
   */
  rullHeismote(sannsynlighet = 0.4): boolean {
    return this.rng() < sannsynlighet;
  }

  /**
   * Avgjør hva som skjer når spilleren går inn i heisen: ingenting,
   * vanlig møte (40 %) — eller konfrontasjon hvis spilleren har
   * signert fra stallen (5D): da venter rivalen ved første anledning.
   */
  avgjorHeismote(
    rivalIVindu: boolean,
    sannsynlighet = 0.4,
  ): 'ingen' | 'mote' | 'konfrontasjon' {
    if (!rivalIVindu) return 'ingen';
    if (this.konfrontasjonVenter) return 'konfrontasjon';
    return this.rullHeismote(sannsynlighet) ? 'mote' : 'ingen';
  }

  /** Fiske-utfall (5C): 50 % hint, ellers avslørt (vekter opp rivalen). */
  rullFisking(): 'hint' | 'avslort' {
    return this.rng() < 0.5 ? 'hint' : 'avslort';
  }

  /** Skal rivalen konfrontere spilleren (etter signering fra stallen, 5D)? */
  get skalKonfrontere(): boolean {
    return this.konfrontasjonVenter;
  }

  markerKonfrontert(): void {
    this.konfrontasjonVenter = false;
  }

  // ---- 5C: informasjonslekkasje og vekting ----

  /** Spilleren fisket og ble avslørt: rival-hendelser vektes opp i N dager. */
  avslort(dag: number, dager = 3): void {
    this.aggressivTilDag = Math.max(this.aggressivTilDag, dag + dager);
  }

  /** 5D: spilleren signerte fra stallen — hevn-modus i N dager + konfrontasjon. */
  utlosHevn(dag: number, dager = 5): void {
    this.hevnTilDag = Math.max(this.hevnTilDag, dag + dager);
    this.konfrontasjonVenter = true;
  }

  /** Vekt-multiplikator for rival-hendelser i data/events.ts. */
  rivalVektFaktor(dag: number): number {
    if (dag <= this.hevnTilDag) return 3;
    if (dag <= this.aggressivTilDag) return 2;
    return 1;
  }

  get erAggressiv(): boolean {
    return this.aggressivTilDag > 0 || this.hevnTilDag > 0;
  }

  // ---- 5C: rivalens bud på manus spilleren har nølt med ----

  leggInnBud(bud: RivalBud): void {
    this.aktivtBud = bud;
  }

  get budet(): RivalBud | null {
    return this.aktivtBud;
  }

  /**
   * Dagsjekk: har budet løpt ut, eller har spilleren matchet?
   * Returnerer utfall som scenen kan vise; null = ingenting skjedde.
   */
  sjekkBud(dag: number, spillerHarSignert: (isbn: string) => boolean):
    | { utfall: 'matchet' | 'tapt'; bud: RivalBud }
    | null {
    if (!this.aktivtBud) return null;
    if (spillerHarSignert(this.aktivtBud.isbn)) {
      const bud = this.aktivtBud;
      this.aktivtBud = null;
      return { utfall: 'matchet', bud };
    }
    if (dag > this.aktivtBud.fristDag) {
      const bud = this.aktivtBud;
      this.aktivtBud = null;
      return { utfall: 'tapt', bud };
    }
    return null;
  }

  // ---- 5C: kolliderende lanseringer ----

  leggTilLansering(lansering: RivalLansering): void {
    this.aktiveLanseringer.push(lansering);
  }

  /** Salgsmultiplikator for spillerens bøker i en sjanger en gitt dag. */
  salgsFaktor(sjanger: string, dag: number): number {
    let faktor = 1;
    for (const l of this.aktiveLanseringer) {
      if (dag <= l.tilDag && l.sjanger.toUpperCase() === sjanger.toUpperCase()) {
        faktor *= l.faktor;
      }
    }
    return faktor;
  }

  ryddUtlopteLanseringer(dag: number): void {
    this.aktiveLanseringer = this.aktiveLanseringer.filter((l) => dag <= l.tilDag);
  }

  // ---- 5D: relasjon med stall-forfatter i kantina ----

  private samtaler = new Map<string, number>();

  registrerSamtale(npcId: string): number {
    const n = (this.samtaler.get(npcId) ?? 0) + 1;
    this.samtaler.set(npcId, n);
    return n;
  }

  antallSamtaler(npcId: string): number {
    return this.samtaler.get(npcId) ?? 0;
  }

  // ---- Persistens ----

  tilLagring(): RivalLagring {
    return {
      besokTeller: this.besokTeller,
      aggressivTilDag: this.aggressivTilDag,
      hevnTilDag: this.hevnTilDag,
      aktivtBud: this.aktivtBud,
      aktiveLanseringer: [...this.aktiveLanseringer],
      konfrontasjonVenter: this.konfrontasjonVenter,
      samtaler: Object.fromEntries(this.samtaler),
    };
  }

  lastFraLagring(lagret: Partial<RivalLagring>): void {
    if (typeof lagret.besokTeller === 'number') this.besokTeller = lagret.besokTeller;
    if (typeof lagret.aggressivTilDag === 'number') {
      this.aggressivTilDag = lagret.aggressivTilDag;
    }
    if (typeof lagret.hevnTilDag === 'number') this.hevnTilDag = lagret.hevnTilDag;
    this.aktivtBud = lagret.aktivtBud ?? null;
    this.aktiveLanseringer = Array.isArray(lagret.aktiveLanseringer)
      ? [...lagret.aktiveLanseringer]
      : [];
    this.konfrontasjonVenter = !!lagret.konfrontasjonVenter;
    this.samtaler = new Map(
      Object.entries(lagret.samtaler ?? {}).filter(
        ([, v]) => typeof v === 'number',
      ) as Array<[string, number]>,
    );
  }
}

export interface RivalLagring {
  besokTeller: number;
  aggressivTilDag: number;
  hevnTilDag: number;
  aktivtBud: RivalBud | null;
  aktiveLanseringer: RivalLansering[];
  konfrontasjonVenter: boolean;
  samtaler: Record<string, number>;
}

/** Delt rival-instans for spilløkta. */
export const rivalen = new RivalSystem();
